import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  message,
  Modal,
} from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import axiosInstance from "../../../../configs"; // untuk API local BE
import Title from "antd/es/typography/Title";

const { Text } = Typography;
const { Option } = Select;

const DOC_OPTIONS = [
  { label: "BC 1.6", value: 16 },
  { label: "BC 2.7", value: 27 },
  { label: "BC 4.0", value: 40 },
];

export default function FormPemasukanBarang() {
  const [form] = Form.useForm();

  const [loadingNopen, setLoadingNopen] = useState(false);
  const [nopenOptions, setNopenOptions] = useState([]); // list nomorDaftar
  const [docListRaw, setDocListRaw] = useState([]); // raw response utk meta

  const [selectedDocMeta, setSelectedDocMeta] = useState(null); // meta header dari pabean
  const [barangRaw, setBarangRaw] = useState([]); // raw barang utk payload details
  const [saving, setSaving] = useState(false);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // total kemasan dari form (watch)
  const barangWatch = Form.useWatch("barang", form);
  const totalKemasan = useMemo(() => {
    if (!Array.isArray(barangWatch)) return 0;
    return barangWatch.reduce(
      (sum, row) => sum + (Number(row?.qtyKemasan) || 0),
      0,
    );
  }, [barangWatch]);

  // init BPB number & date
  useEffect(() => {
    // const initialBpb = generateBpbNumber(bpbCounterRef);
    // form.setFieldsValue({
    //   bpb_number: initialBpb,
    //   bpb_date: dayjs(),
    // });
    // Keep empty or set only date if desired, but user wants link to nopen.
  }, [form]);

  // ============== 1. ON CHANGE JENIS DOKUMEN PABEAN ==================
  const handleJenisDokumenChange = async (kodeDokumen) => {
    form.setFieldsValue({
      nopen_number: undefined,
      document_date: undefined,
      supplier_name: undefined,
      owner_name: undefined,
    });
    setSelectedDocMeta(null);
    setBarangRaw([]);
    form.setFieldsValue({ barang: [] });

    if (!kodeDokumen) {
      setNopenOptions([]);
      setDocListRaw([]);
      return;
    }

    setLoadingNopen(true);
    try {
      // 1. Ambil semua dokumen yang SUDAH pernah dibuat di sistem lokal
      // Gunakan composite key: nomor_aju + document_number + document_date agar unik
      let existingKeys = new Set();
      try {
        const incomingRes = await axiosInstance.get("/incoming", {
          params: {
            page: 1,
            limit: 9999, // biar aman ambil semua
          },
        });

        const incomingData = incomingRes.data?.data || [];
        existingKeys = new Set(
          incomingData.map((row) => {
            const nomorAju = String(row.nomor_aju || "").trim();
            const nopen = String(row.document_number || "").trim();
            const date = row.document_date
              ? dayjs(row.document_date).format("YYYY-MM-DD")
              : "";
            return `${nomorAju}|${nopen}|${date}`;
          }),
        );
      } catch (e) {
        console.error("Gagal cek incoming, tetap lanjut tanpa filter:", e);
      }

      // 2. Ambil list Nopen dari API Pabean
      // let kodeRespon = "";
      // switch (String(kodeDokumen)) {
      //   case "16":
      //     kodeRespon = "1603";
      //     break;
      //   case "27":
      //     kodeRespon = "2703";
      //     break;
      //   case "28":
      //     kodeRespon = "2803";
      //     break;
      //   case "33":
      //     kodeRespon = "3303";
      //     break;
      //   case "40":
      //     kodeRespon = "4003";
      //     break;
      //   case "331":
      //     kodeRespon = "33103";
      //     break;
      //   default:
      //     kodeRespon = "1603";
      //     break;
      // }

      const res = await axiosInstance.get("/portal/pasifik/get-data-nopen", {
        params: {
          kodeDokumen,
          // kodeRespon,
        },
      });

      // Support both wrapped and unwrapped responses
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.status === "OK"
          ? res.data.data
          : [];

      if (!list || list.length === 0) {
        message.warning("Tidak ada data Nopen");
        setNopenOptions([]);
        setDocListRaw([]);
        return;
      }

      setDocListRaw(list);

      // 3. Build options, sambil FILTER yang sudah ada di incoming
      // Gunakan composite key: nopen + tanggal_daftar agar unik
      const options = list
        .map((doc) => {
          const headerId = doc.id; // Unique header ID
          const nomorDaftar =
            doc.dataRespon?.[0]?.nomorDaftar ||
            doc.nomorDaftar ||
            doc.nomor_daftar ||
            "";
          const nomorAju = doc.nomorAju || doc.nomor_aju || ""; // nomor_aju per dokumen
          const tanggalDaftar =
            doc.dataRespon?.[0]?.tanggalDaftar ||
            doc.tanggalDaftar ||
            doc.tanggal_daftar ||
            "";
          const pemilikBarang =
            doc.entitas?.[2]?.namaEntitas || doc.nama_entitas || "";

          return {
            id: headerId, // Use unique ID as value
            nomorDaftar: String(nomorDaftar || "").trim(),
            nomorAju: String(nomorAju || "").trim(),
            tanggalDaftar: tanggalDaftar,
            label: `${nomorDaftar} - ${pemilikBarang} (${dayjs(
              tanggalDaftar,
            ).format("DD MMM YYYY")})`,
            value: headerId, // Use ID as unique value
            // Keep original doc for handleNopenChange
            originalDoc: doc,
          };
        })
        // FILTER: composite key nomor_aju + nopen + tanggal_daftar
        .filter((opt) => {
          const nomorAju = String(opt.nomorAju || "").trim();
          const nopen = String(opt.nomorDaftar || "").trim();
          const date = opt.tanggalDaftar
            ? dayjs(opt.tanggalDaftar).format("YYYY-MM-DD")
            : "";
          const key = `${nomorAju}|${nopen}|${date}`;
          return !existingKeys.has(key);
        });

      setNopenOptions(options);
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar Nopen");
      setNopenOptions([]);
      setDocListRaw([]);
    } finally {
      setLoadingNopen(false);
    }
  };

  // ============== 2. ON CHANGE NO DOKUMEN PABEAN ==================
  const handleNopenChange = async (headerId) => {
    if (!headerId) {
      setSelectedDocMeta(null);
      setBarangRaw([]);
      form.setFieldsValue({ barang: [] });
      return;
    }

    // Find document by unique ID
    const doc = docListRaw.find((d) => d.id === headerId);

    if (!doc) {
      message.error("Data dokumen tidak ditemukan");
      return;
    }

    // ambil meta yang dibutuhkan
    const nomorPendaftaran =
      doc.dataRespon?.[0]?.nomorDaftar ||
      doc.nomorDaftar ||
      doc.nomor_daftar ||
      "";
    const nomorAju = doc.nomorAju || doc.nomor_aju || null;
    const tanggalDaftar =
      doc.dataRespon?.[0]?.tanggalDaftar ||
      doc.tanggalDaftar ||
      doc.tanggal_daftar;
    const kodeDokumen = doc.kodeDokumen || doc.kode_dokumen;
    const kodeValuta = doc.kodeValuta || doc.kode_valuta;
    const kodeNegaraAsal = doc.kodeNegaraAsal || doc.kode_negara_asal || null;
    const cifHeader = doc.cif ?? null;

    const kontainerList = (doc.kontainer || []).map((k) => k.nomorKontainer);
    const kemasan0 = (doc.kemasan || [])[0] || {};
    const merkKemasan = kemasan0.merkKemasan || "";
    const totalKemasanHeader = kemasan0.jumlahKemasan || 0;
    const dokRaw5 = doc.dokumen?.[4] || null;

    const blDate = dokRaw5?.tanggalDokumen || null;
    const blNumber = dokRaw5?.nomorDokumen || null;

    // entitas: 0 = pemasok, 2 = pemilik (sesuai ketentuan)
    const pemasok = doc.entitas?.[0];
    const pemilik = doc.entitas?.[2];
    const pemilikNama = pemilik?.namaEntitas || doc.nama_entitas || "";

    const meta = {
      headerId, // Store the unique header ID
      nomorPendaftaran,
      nomorAju,
      tanggalDaftar,
      kodeDokumen,
      kodeValuta,
      kodeNegaraAsal,
      cif: cifHeader,
      kontainerList,
      merkKemasan,
      totalKemasanHeader,
      pemasokNomorIdentitas: pemasok?.nomorIdentitas || null,
      pemilikNomorIdentitas:
        pemilik?.nomorIdentitas || doc.nomor_identitas || null,
      pemasokNama: pemasok?.namaEntitas || "",
      pemilikNama: pemilikNama,
      blNumber,
      blDate,
    };

    setSelectedDocMeta(meta);

    // set ke form: tanggal dokumen, pemasok, pemilik, dan generate BPB Number
    const generatedBpb = `BPB-${dayjs().format("YYYYMMDD")}-${nomorPendaftaran}`;

    form.setFieldsValue({
      bpb_number: generatedBpb,
      bpb_date: dayjs(),
      document_date: tanggalDaftar ? dayjs(tanggalDaftar) : undefined,
      supplier_name: meta.pemasokNama,
      owner_name: meta.pemilikNama,
      bl_number: meta.blNumber,
      bl_date: meta.blDate ? dayjs(meta.blDate) : undefined,
    });

    // Fetch barang by unique header ID instead of nomorPendaftaran
    await fetchBarangByHeaderId(headerId);
  };

  // ============== 3. FETCH BARANG DARI API BARANG (by Header ID) ===================
  const fetchBarangByHeaderId = async (headerId) => {
    try {
      const res = await axiosInstance.get(
        "/portal/pasifik/get-data-barang-by-header-id",
        {
          params: {
            headerId,
          },
        },
      );

      // Support both wrapped and unwrapped responses
      const rows = Array.isArray(res.data)
        ? res.data
        : res.data?.status === "OK"
          ? res.data.data
          : [];

      if (!rows || rows.length === 0) {
        message.warning("Tidak ada data barang.");
        form.setFieldsValue({ barang: [] });
        setBarangRaw([]);
        return;
      }

      // Transform snake_case to camelCase to match existing logic
      const mappedRows = rows.map((r) => ({
        ...r,
        seriBarang: r.seri_barang,
        kodeBarang: r.kode_barang,
        jumlahKemasan: r.jumlah_kemasan,
        kodeJenisKemasan: r.kode_kemasan,
        jumlahSatuan: r.jumlah_satuan,
        kodeSatuanBarang: r.kode_satuan,
        cifRupiah: r.cif_rupiah,
        merk: r.merek,
      }));

      setBarangRaw(mappedRows);

      // Extract Supplier & BL info from the first row (since it's joined data)
      if (mappedRows.length > 0) {
        const firstRow = mappedRows[0];

        let entitasList = [];
        try {
          entitasList =
            typeof firstRow.entitas_list === "string"
              ? JSON.parse(firstRow.entitas_list)
              : firstRow.entitas_list || [];
        } catch (e) {
          console.error("Error parsing entitas_list", e);
        }

        const supplierObj = entitasList.find((e) =>
          ["9", "10"].includes(String(e.kode_entitas)),
        );
        const ownerObj = entitasList.find((e) =>
          ["1", "7", "8"].includes(String(e.kode_entitas)),
        );

        // Update meta with info from barang/header join
        setSelectedDocMeta((prev) => ({
          ...prev,
          kodeValuta: firstRow.kode_valuta || prev?.kodeValuta,
          cif: firstRow.cif || prev?.cif,
          kodeNegaraAsal: firstRow.kode_negara_asal || prev?.kodeNegaraAsal,
          pemasokNama: supplierObj?.nama_entitas || prev?.pemasokNama,
          pemilikNama: ownerObj?.nama_entitas || prev?.pemilikNama,
        }));

        form.setFieldsValue({
          supplier_name:
            supplierObj?.nama_entitas || form.getFieldValue("supplier_name"),
          owner_name:
            ownerObj?.nama_entitas || form.getFieldValue("owner_name"),
          bl_number: firstRow.nomor_bl || form.getFieldValue("bl_number"),
          bl_date: firstRow.tanggal_bl
            ? dayjs(firstRow.tanggal_bl)
            : form.getFieldValue("bl_date"),
        });
      }

      // map ke Form.List barang
      const barangForForm = mappedRows.map((row) => ({
        seri: row.seriBarang,
        namaBarang: `${row.uraian} (${row.kodeBarang})`,
        qtyKemasan: row.jumlahKemasan,
        kemasan: row.kodeJenisKemasan,
        qtySatuan: row.jumlahSatuan,
        satuan: row.kodeSatuanBarang,
      }));

      form.setFieldsValue({ barang: barangForForm });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil data barang");
      setBarangRaw([]);
      form.setFieldsValue({ barang: [] });
    }
  };

  // ============== 4. SUBMIT KE API LOCAL ===================
  const onFinish = async (values) => {
    if (!selectedDocMeta) {
      message.error(
        "Silakan pilih Jenis Dokumen & No Dokumen Pabean terlebih dahulu.",
      );
      return;
    }

    if (!Array.isArray(barangRaw) || barangRaw.length === 0) {
      message.error("Data barang belum tersedia.");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setSaving(true);
      const values = pendingValues;

      const { bpb_number, bpb_date, bl_number, bl_date } = values;

      const meta = selectedDocMeta;

      const header = {
        id_contact: meta.pemasokNama || "-",
        id_customer: meta.pemilikNomorIdentitas
          ? String(meta.pemilikNomorIdentitas)
              .replace(/[^0-9]/g, "")
              .padStart(16, "0")
              .slice(1, 16)
          : "-",
        id_document: meta.kodeDokumen || null,
        id_cif_currency: meta.kodeValuta || null,
        bpb_number: bpb_number || null,
        bpb_date: bpb_date ? dayjs(bpb_date).format("YYYY-MM-DD") : null,
        container_number: (meta.kontainerList || []).join(",") || "-",
        document_number: meta.nomorPendaftaran || null,
        nomor_aju: meta.nomorAju || null,
        document_date: meta.tanggalDaftar
          ? dayjs(meta.tanggalDaftar).format("YYYY-MM-DD")
          : null,
        country_code: meta.kodeNegaraAsal || null,
        bl_date: bl_date ? dayjs(bl_date).format("YYYY-MM-DD") : null,
        purchase_number: bl_number || null,
        cif: meta.cif || null,
        packaging_merk: meta.merkKemasan || null,
        total_packaging: totalKemasan || meta.totalKemasanHeader || 0,
        stripping: 0,
        lampiran_1: null,
        lampiran_2: null,
        created_by: 0,
        updated_by: 0,
      };

      const formBarang = values.barang || [];

      const details = barangRaw.map((row, index) => {
        const formRow = formBarang[index] || {};

        return {
          nopen: meta.nomorPendaftaran,
          tanggal_nopen: meta.tanggalDaftar
            ? dayjs(meta.tanggalDaftar).format("YYYY-MM-DD")
            : null,
          seri: row?.seriBarang ? Number(row.seriBarang) : index + 1, // Sequential seri: 1, 2, 3...
          item_code: row.kodeBarang,
          merk: row.merk,
          id_currency: meta.kodeValuta || null,
          satuan: formRow.satuan || row.kodeSatuanBarang || null,
          total_satuan: Number(formRow.qtySatuan) || row.jumlahSatuan || 0,
          sub_satuan: "-",
          quantity: 0,
          total_price: row.cifRupiah ?? meta.cif ?? null,
          kemasan: formRow.kemasan || row.kodeJenisKemasan | null,
          total_kemasan: Number(formRow.qtyKemasan) || row.jumlahKemasan | 0,
          created_by: 0,
          updated_by: 0,
        };
      });

      const payload = {
        header,
        details,
      };

      await axiosInstance.post("/incoming", payload);

      message.success("Pemasukan barang berhasil disimpan.");
      setConfirmVisible(false);
      setPendingValues(null);
      // Redirect to incoming list
      window.location.href = "/#/inventory/incoming";
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan pemasukan barang.");
    } finally {
      setSaving(false);
    }
  };

  // ============== RENDER ==============
  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Form Pemasukan Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pemasukan Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
      </Row>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* BARIS: BPB */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No BPB</Text>}
              name="bpb_number"
              rules={[{ required: true, message: "No BPB wajib diisi" }]}
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal BPB</Text>}
              name="bpb_date"
              rules={[{ required: true, message: "Tanggal BPB wajib diisi" }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS: Dok Pabean */}
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label={<Text strong>Jenis Dokumen Pabean</Text>}
              name="id_document"
              rules={[
                { required: true, message: "Jenis dokumen wajib dipilih" },
              ]}
            >
              <Select
                placeholder="Pilih Jenis Dokumen"
                options={DOC_OPTIONS}
                onChange={handleJenisDokumenChange}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label={<Text strong>No Dokumen Pabean</Text>}
              name="nopen_number"
              rules={[{ required: true, message: "No dokumen wajib dipilih" }]}
            >
              <Select
                placeholder="Pilih atau Cari No Dokumen"
                loading={loadingNopen}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={handleNopenChange}
              >
                {nopenOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label={<Text strong>Tanggal Dokumen Pabean</Text>}
              name="document_date"
            >
              <DatePicker
                disabled
                style={{ width: "100%" }}
                format="DD MMM YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS: Pemasok & Pemilik */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Pemasok / Pengirim</Text>}
              name="supplier_name"
            >
              <Input disabled placeholder="Pemasok / Pengirim" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Pemilik Barang</Text>}
              name="owner_name"
            >
              <Input disabled placeholder="Pemilik Barang" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS: BL */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nomor BL</Text>}
              name="bl_number"
              rules={[{ required: true, message: "Nomor BL wajib diisi" }]}
            >
              <Input placeholder="Nomor BL" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal BL</Text>}
              name="bl_date"
              rules={[{ required: true, message: "Tanggal BL wajib diisi" }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>

        {/* Lampiran (dummy – bisa kamu sambungin ke upload BE) */}
        <Row style={{ marginTop: 8, marginBottom: 16 }}>
          <Col span={24}>
            <Text strong>Lampiran</Text>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
                onClick={() => {
                  document.getElementById("upload-lampiran-pemasukan")?.click();
                }}
              >
                Upload File
              </Button>
              <input
                id="upload-lampiran-pemasukan"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    message.info(
                      `File "${file.name}" terpilih (proses upload bisa kamu sambungkan ke BE).`,
                    );
                  }
                }}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF | DOC | DOCX | XLS |
                XLSX
                <br />
                Ukuran Maksimal File: 5 MB.
              </Text>
            </Space>
          </Col>
        </Row>

        {/* TABEL BARANG */}
        <Card
          bodyStyle={{ padding: 0 }}
          style={{
            marginTop: 8,
            borderRadius: 2,
            border: "1px solid #E4E6EF",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "3fr 0.8fr 1.4fr 1.2fr 1.4fr 1.1fr 60px",
              background: "#F3F6F9",
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <div>NAMA BARANG *</div>
            <div>SERI</div>
            <div>QTY KEMASAN</div>
            <div>KEMASAN</div>
            <div>QTY SATUAN</div>
            <div>SATUAN</div>
            <div style={{ textAlign: "center" }}>AKSI</div>
          </div>

          <Form.List name="barang">
            {(fields, { remove }) => (
              <>
                {fields.map((field) => (
                  <div
                    key={field.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "3fr 0.8fr 1.4fr 1.2fr 1.4fr 1.1fr 60px",
                      padding: "8px 16px",
                      borderTop: "1px solid #E4E6EF",
                      alignItems: "center",
                    }}
                  >
                    {/* Nama Barang */}
                    <Form.Item
                      {...field}
                      name={[field.name, "namaBarang"]}
                      fieldKey={[field.fieldKey, "namaBarang"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input placeholder="Nama Barang" />
                    </Form.Item>

                    {/* Seri */}
                    <Form.Item
                      {...field}
                      name={[field.name, "seri"]}
                      fieldKey={[field.fieldKey, "seri"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input placeholder="Seri" />
                    </Form.Item>

                    {/* Qty Kemasan */}
                    <Form.Item
                      {...field}
                      name={[field.name, "qtyKemasan"]}
                      fieldKey={[field.fieldKey, "qtyKemasan"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input type="number" placeholder="Qty Kemasan" />
                    </Form.Item>

                    {/* Kemasan */}
                    <Form.Item
                      {...field}
                      name={[field.name, "kemasan"]}
                      fieldKey={[field.fieldKey, "kemasan"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input placeholder="Kemasan" />
                    </Form.Item>

                    {/* Qty Satuan */}
                    <Form.Item
                      {...field}
                      name={[field.name, "qtySatuan"]}
                      fieldKey={[field.fieldKey, "qtySatuan"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input type="number" placeholder="Qty Satuan" />
                    </Form.Item>

                    {/* Satuan */}
                    <Form.Item
                      {...field}
                      name={[field.name, "satuan"]}
                      fieldKey={[field.fieldKey, "satuan"]}
                      style={{ marginBottom: 0, paddingRight: 8 }}
                    >
                      <Input placeholder="Satuan" />
                    </Form.Item>

                    <div style={{ textAlign: "center" }}>
                      <Button
                        type="text"
                        icon={<DeleteOutlined style={{ color: "#F64E60" }} />}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  </div>
                ))}

                {fields.length === 0 && (
                  <div
                    style={{
                      padding: "12px 16px",
                      borderTop: "1px solid #E4E6EF",
                      color: "#B5B5C3",
                      fontStyle: "italic",
                    }}
                  >
                    Data barang akan terisi otomatis setelah memilih No Dokumen
                    Pabean.
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Card>

        {/* TOTAL KEMASAN */}
        <div
          style={{
            marginTop: 12,
            textAlign: "right",
            fontWeight: 600,
          }}
        >
          Total Kemasan: {totalKemasan}
        </div>

        {/* ACTION BUTTONS */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Space>
            <Button danger>Batal</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{
                minWidth: 100,
                fontWeight: 600,
              }}
            >
              Simpan
            </Button>
          </Space>
        </Row>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Penyimpanan"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>
              Apakah Anda yakin ingin menyimpan data pemasukan barang berikut?
            </p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>No BPB</td>
                  <td style={{ padding: 4 }}>: {pendingValues.bpb_number}</td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>
                    Tanggal BPB
                  </td>
                  <td style={{ padding: 4 }}>
                    : {dayjs(pendingValues.bpb_date).format("DD MMMM YYYY")}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>No Dokumen</td>
                  <td style={{ padding: 4 }}>
                    : {selectedDocMeta?.nomorPendaftaran}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>Pemasok</td>
                  <td style={{ padding: 4 }}>
                    : {pendingValues.supplier_name}
                  </td>
                </tr>
              </tbody>
            </table>

            <Text strong>Ringkasan Barang:</Text>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {(pendingValues.barang || []).map((item, idx) => (
                <li key={idx} style={{ marginBottom: 8 }}>
                  <div>
                    {item.namaBarang} - <b>{item.qtyKemasan}</b> {item.kemasan}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </Card>
  );
}
